import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Components
import InputOTP from 'components/form/InputOTP';
import Button from 'components/form/Button';

// Utils
import {
  getDataFromLocalStorage,
  getUserType,
  objectToFormData,
} from 'utils/helpers';

// Redux Actions
import {
  fetchProfile,
  handelSendOTP,
  showSuccess,
  verifyOTP,
} from 'store/slices';

const EnterOTP = ({
  setIsOTPSent,
  isOTPSent,
  counter,
  setCounter,
  userEmail,
}) => {
  // Navigation and state management
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const memberType = getUserType();

  // Component state
  const [resetOTP, setResetOTP] = useState(true);
  const [otp, setOTP] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Derived data
  const userDetails = getDataFromLocalStorage();
  const isOTPValid = otp.length === 6;
  const formattedTime = `${String(Math.floor(counter / 60)).padStart(
    2,
    '0'
  )}:${String(counter % 60).padStart(2, '0')}`;

  // Handle OTP timer countdown
  useEffect(() => {
    let timer;

    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), 1000);
    } else {
      setIsOTPSent(false);
    }

    return () => clearTimeout(timer);
  }, [counter, setIsOTPSent]);

  // OTP input handler
  const handleOtpChange = (newOtp) => {
    if (newOtp) {
      setOTP(newOtp);
    }
  };

  // Verify OTP submission
  const handleVerifyOTP = async () => {
    if (!isOTPValid) return;

    setIsVerifying(true);

    try {
      const formData = objectToFormData({
        user_id: userDetails?.id,
        otp: otp,
      });

      const response = await dispatch(verifyOTP(formData));

      if (response?.status === 200) {
        dispatch(showSuccess(response.message));
        navigate(`/${memberType}/dashboard`);
        await dispatch(fetchProfile());
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP handler
  const handleResendOtp = async () => {
    setIsResending(true);

    try {
      const response = await dispatch(
        handelSendOTP(objectToFormData({ email: userEmail }))
      );

      if (response?.status === 200) {
        setIsOTPSent(true);
        setCounter(600);
        setResetOTP(true);
      }
    } finally {
      setIsResending(false);
    }
  };

  // Cancel handler
  const handleCancel = () => {
    navigate(-1);
  };

  // Form submission handler
  const handleFormSubmit = (e) => {
    if (e.key === 'Enter') {
      handleVerifyOTP();
    }
  };

  return (
    <div className="container">
      <div className="p-5 text-center">
        <div className="position-relative mb-3">
          <h1 className="text-20-500 color-title-navy">Enter OTP</h1>
        </div>

        <Formik initialValues={{}} onSubmit={handleVerifyOTP}>
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit} onKeyDown={handleFormSubmit}>
              <div className="center-flex flex-column">
                <InputOTP
                  blockSize={6}
                  resetOTP={resetOTP}
                  onSubmit={handleOtpChange}
                  setResetOTP={setResetOTP}
                />
              </div>

              {/* OTP Status Messages */}
              <div className="mt-5 mb-4">
                {isOTPSent && (
                  <div className="text-center text-14-400">OTP sent!</div>
                )}

                {counter === 0 ? (
                  <div className="d-flex justify-content-center">
                    If you didn't receive a code!
                    <span
                      className="text-decoration-underline ps-1"
                      role="button"
                      style={{ color: '#264cbf' }}
                      onClick={handleResendOtp}
                      disabled={isResending}
                    >
                      {isResending ? 'Sending...' : 'Resend'}
                    </span>
                  </div>
                ) : (
                  <div className="text-center text-16-400 mt-2">
                    You can resend the OTP in
                    <span className="color-new-car text-16-500">
                      {` ${formattedTime}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="ps-5 pe-5 d-flex justify-content-center align-items-center gap-3">
                <Button
                  text="Verify"
                  btnStyle="primary-dark"
                  className="h-37 text-14-500 cps-30 cpe-30"
                  onClick={handleVerifyOTP}
                  disabled={!isOTPValid || isVerifying || isResending}
                  btnLoading={isVerifying}
                />
                <Button
                  text="Cancel"
                  btnStyle="light-outline"
                  className="h-37 text-14-500 cps-20 cpe-20"
                  onClick={handleCancel}
                  disabled={isResending || isVerifying}
                />
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EnterOTP;
